import { useCallback, useMemo, useRef, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Table } from "@common/Table";
import { ConfirmDialog } from "@common/ConfirmDialog";
import { Badge, Button, ErrorState, Title } from "@common/index";
import { ComponentColor, ComponentSize } from "@common/types";
import { usePromotionApi } from "@/hooks/apiHooks/usePromotionApi";
import { useMountRead } from "@/hooks/useMountRead";
import { formatDateTime } from "@utils/formatter";
import { PROMOTION_SLOT_LABELS, type Promotion } from "@models/promotion";
import { PromotionFormDialog } from "./_components/PromotionFormDialog";
import { formatPromotionWindow } from "./_helpers/promotionDates";

type Dialog =
    | { kind: "create" }
    | { kind: "edit"; promotion: Promotion }
    | { kind: "deactivate"; promotion: Promotion };

// E14-S4 (#248): the admin list of home-page banner promotions, with a shared
// Create/Edit modal (DecisionLog §4.14). Delete on the API is a soft delete,
// so the row action is "Deactivate" and a deactivated row stays listed,
// marked, with Edit to turn it back on.
export const Promotions = () => {
    const { getPromotions, deletePromotion } = usePromotionApi();
    // Null until a read has answered, so "no rows yet" is never shown as "no
    // promotions".
    const [promotions, setPromotions] = useState<Promotion[] | null>(null);
    const [dialog, setDialog] = useState<Dialog | null>(null);
    const [isDeactivating, setIsDeactivating] = useState(false);
    const [deactivateFailed, setDeactivateFailed] = useState(false);
    // Whether a list is on screen. With none, a failed read is shown as the
    // failed state and the toast is skipped (one failure, one alert); with
    // one, the rows stay and the toast is what reports the failure. Set from
    // the read's answer, never during render.
    const hasList = useRef(false);

    // The read THIS mount issued decides loading/failed (#407).
    const readPromotions = useCallback(
        (isCurrent: () => boolean) =>
            getPromotions({ skipErrorNotification: !hasList.current }).then(
                (rows) => {
                    if (isCurrent()) {
                        hasList.current = true;
                        setPromotions(rows);
                    }
                },
            ),
        [getPromotions],
    );
    const { readStatus, reload } = useMountRead(readPromotions);
    const refetch = useCallback(() => void reload(), [reload]);

    // Rows on screen while a re-read is out, or after one failed (a 4xx/5xx
    // or an answer that is not a list), may be stale. Edit seeds a
    // full-replacement PUT from the row, so a stale row would silently revert
    // the change just made; the actions on the list wait for a re-read to
    // land (review F1/F2).
    const isRefreshing = promotions !== null && readStatus === "loading";
    const isStale = promotions !== null && readStatus === "failed";
    const actionsHeld = isRefreshing || isStale;

    const closeDialog = useCallback(() => {
        setDialog(null);
        setDeactivateFailed(false);
    }, []);

    const handleDeactivate = async (promotion: Promotion) => {
        setDeactivateFailed(false);
        setIsDeactivating(true);
        try {
            await deletePromotion(promotion.id);
        } catch {
            // useApi has toasted; the confirmation stays open and says so.
            setDeactivateFailed(true);
            setIsDeactivating(false);
            return;
        }
        setIsDeactivating(false);
        closeDialog();
        refetch();
    };

    const columns = useMemo<ColumnDef<Promotion>[]>(
        () => [
            {
                accessorKey: "slot",
                header: "Slot",
                enableSorting: false,
                cell: ({ row }) => (
                    <Badge color={ComponentColor.Info} size={ComponentSize.SM}>
                        {PROMOTION_SLOT_LABELS[row.original.slot] ??
                            `Slot ${row.original.slot}`}
                    </Badge>
                ),
            },
            {
                accessorKey: "label",
                header: "Label",
                enableSorting: false,
                cell: ({ row }) => (
                    <div className="flex items-center gap-2">
                        <span className="font-semibold">{row.original.label}</span>
                        {!row.original.isActive && (
                            <Badge
                                color={ComponentColor.Neutral}
                                size={ComponentSize.SM}
                            >
                                Inactive
                            </Badge>
                        )}
                    </div>
                ),
            },
            {
                id: "dates",
                // The last day shown, not the API's exclusive end.
                header: "Active dates (UTC)",
                enableSorting: false,
                cell: ({ row }) =>
                    formatPromotionWindow(
                        row.original.startsAt,
                        row.original.endsAt,
                    ),
            },
            {
                id: "image",
                header: "Image",
                enableSorting: false,
                cell: ({ row }) => (row.original.imageUrl ? "Yes" : "No"),
            },
            {
                id: "lastUpdated",
                header: "Last updated",
                enableSorting: false,
                // The viewer's local time, with the zone named: the dates
                // beside it are UTC (review F6).
                cell: ({ row }) =>
                    formatDateTime(
                        row.original.updatedAt ?? row.original.createdAt,
                        { timeZoneName: "short" },
                    ),
            },
            {
                id: "actions",
                header: () => <span className="sr-only">Actions</span>,
                enableSorting: false,
                cell: ({ row }) => (
                    // Every row has the same buttons, so each accessible
                    // name carries the row it acts on.
                    <div className="flex gap-2">
                        <Button
                            color={ComponentColor.Secondary}
                            size={ComponentSize.XS}
                            outline
                            disabled={actionsHeld}
                            aria-label={`Edit ${row.original.label}`}
                            onClick={() =>
                                setDialog({
                                    kind: "edit",
                                    promotion: row.original,
                                })
                            }
                        >
                            Edit
                        </Button>
                        {/* Already inactive: Edit reactivates it. */}
                        {row.original.isActive && (
                            <Button
                                color={ComponentColor.Error}
                                size={ComponentSize.XS}
                                outline
                                disabled={actionsHeld}
                                aria-label={`Deactivate ${row.original.label}`}
                                onClick={() =>
                                    setDialog({
                                        kind: "deactivate",
                                        promotion: row.original,
                                    })
                                }
                            >
                                Deactivate
                            </Button>
                        )}
                    </div>
                ),
            },
        ],
        [actionsHeld],
    );

    const table = (
        <Table
            data={promotions ?? []}
            columns={columns}
            size={ComponentSize.XS}
            isLoading={promotions === null}
        />
    );

    return (
        <div className="px-10">
            <div className="flex items-center justify-between gap-4">
                <Title>Promotions</Title>
                <Button
                    color={ComponentColor.Secondary}
                    size={ComponentSize.SM}
                    disabled={actionsHeld}
                    onClick={() => setDialog({ kind: "create" })}
                >
                    + New Promotion
                </Button>
            </div>
            <p className="text-neutral/80 max-w-3xl">
                The banners on the storefront's browse home: one hero, one
                mid-page banner and three tiles. A promotion is an image, a
                label and a link; it never changes a price.
            </p>

            {isRefreshing && (
                <p role="status" className="mt-3 text-sm text-neutral/80">
                    Refreshing…
                </p>
            )}
            {isStale && (
                // Lasting, not just the toast: until a re-read lands, what is
                // shown may not be what is stored.
                <div
                    role="alert"
                    className="alert alert-warning mt-3 flex items-center justify-between gap-4"
                >
                    <span>
                        This list may be out of date. Couldn't refresh the
                        promotions, so editing is paused until it reloads.
                    </span>
                    <Button
                        color={ComponentColor.Neutral}
                        size={ComponentSize.SM}
                        onClick={refetch}
                    >
                        Retry
                    </Button>
                </div>
            )}

            {promotions === null && readStatus === "failed" ? (
                // A failed read is not an empty list.
                <ErrorState
                    title="Couldn't load the promotions"
                    message="The promotion list is unavailable. This is not the same as there being no promotions."
                    onRetry={refetch}
                />
            ) : promotions !== null && promotions.length === 0 ? (
                <div className="text-center text-neutral p-8">
                    <p className="font-semibold">No promotions yet.</p>
                    <p className="text-neutral/80">
                        Use + New Promotion to add a banner.
                    </p>
                </div>
            ) : (
                table
            )}

            {dialog?.kind === "create" && (
                <PromotionFormDialog
                    promotion={null}
                    onClose={closeDialog}
                    onSaved={refetch}
                />
            )}
            {dialog?.kind === "edit" && (
                <PromotionFormDialog
                    key={dialog.promotion.id}
                    promotion={dialog.promotion}
                    onClose={closeDialog}
                    onSaved={refetch}
                />
            )}
            {dialog?.kind === "deactivate" && (
                <ConfirmDialog
                    title="Deactivate this promotion?"
                    body={`“${dialog.promotion.label}” stops showing on the storefront. You can turn it back on from Edit.`}
                    confirmLabel="Deactivate"
                    confirmColor={ComponentColor.Error}
                    dismissLabel="Keep promotion"
                    isProcessing={isDeactivating}
                    errorMessage={
                        deactivateFailed
                            ? "The promotion wasn't deactivated. Try again."
                            : null
                    }
                    onConfirm={() => void handleDeactivate(dialog.promotion)}
                    onClose={closeDialog}
                />
            )}
        </div>
    );
};
